import React from 'react';
import type { IconKind } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { GROWTH_MAP_PACK_META } from '../../growth-map-pack/config';
import {
  MP_ATTACK,
  MP_BENCH,
  MP_COMP_PROFILE,
  MP_KPIS,
  MP_PLAYBOOK,
  MP_REVIEW_AUDIT,
  MP_THREAT,
  MP_YOUR_PROFILE,
} from '../../growth-map-pack/mockData';
import type { MpKpi, MpMapPackData, MpProfileCompleteness, MpProfileItem, MpToneKey } from '../../growth-map-pack/types';
import './growth-map-pack.css';

const MP_TONE: Record<MpToneKey, { accent: string; tint: string }> = {
  ai: { accent: 'var(--ai-3)', tint: 'var(--ai-tint)' },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: { accent: 'var(--terracotta)', tint: 'var(--terracotta-tint)' },
};

function resolveMpTone(tone: MpToneKey) {
  return MP_TONE[tone] ?? MP_TONE.info;
}

const mpEyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink-3)]';

const BENCH_COLS = '1.5fr 1fr 1fr 1fr';

function MpStar({ size = 11, color = 'var(--gold)' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} className="shrink-0">
      <path d="M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.6 5.9 20.4l1.4-6.8L2.2 9l6.9-.7z" />
    </svg>
  );
}

function formatCell(value: string | null | undefined): string {
  if (value == null || value.trim() === '') return '—';
  return value;
}

function MpCard({
  children,
  className = '',
  padding = true,
}: {
  children: React.ReactNode;
  className?: string;
  padding?: boolean;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        padding ? 'p-[22px]' : '',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function MpHead({
  icon,
  tone = 'ai',
  title,
  sub,
}: {
  icon: IconKind;
  tone?: MpToneKey;
  title: string;
  sub?: string;
}) {
  const t = resolveMpTone(tone);
  return (
    <div className="mb-[18px]">
      <div className="flex items-center gap-2.5">
        <span
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{ background: t.tint, color: t.accent }}
        >
          <Icon kind={icon} size={15} stroke={1.9} />
        </span>
        <span className="font-[var(--font-display)] text-[16.5px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          {title}
        </span>
      </div>
      {sub ? (
        <div className="mt-[5px] text-xs leading-[1.5] text-[var(--ink-3)]" style={{ marginLeft: 38 }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

function MpChips({ items, tone }: { items: string[]; tone: MpToneKey }) {
  const t = resolveMpTone(tone);
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <span
          key={item}
          className="rounded-[var(--r-pill)] px-[11px] py-1 text-[11.5px] font-semibold"
          style={{ background: t.tint, color: t.accent }}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function MpVectorLabel({ tone, children }: { tone: MpToneKey; children: React.ReactNode }) {
  const t = resolveMpTone(tone);
  return (
    <div className="mb-2 flex items-center gap-1.5">
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: t.accent }} />
      <span
        className="text-[10.5px] font-bold uppercase tracking-[0.07em]"
        style={{ color: t.accent }}
      >
        {children}
      </span>
    </div>
  );
}

function MpProfileList({ items }: { items: MpProfileItem[] }) {
  return (
    <div className="mt-2 flex flex-col gap-[7px]">
      {items.map((item) => {
        const tone = item.done ? MP_TONE.success : MP_TONE.terra;
        return (
          <div
            key={item.label}
            className="flex items-center gap-2.5 rounded-[var(--r-md)] px-3 py-2"
            style={{ background: tone.tint }}
          >
            <span
              className="inline-flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-full text-white"
              style={{ background: tone.accent }}
            >
              <Icon kind={item.done ? 'check' : 'x'} size={11} stroke={3} />
            </span>
            <span className="flex-1 text-[12.5px] text-[var(--ink-1)]">{item.label}</span>
            {item.detail ? (
              <span className="text-[11px] font-semibold" style={{ color: tone.accent }}>
                {item.detail}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function BenchCell({
  text,
  win,
  star,
  pending,
}: {
  text: string | null;
  win: boolean;
  star?: boolean;
  pending?: boolean;
}) {
  const display = formatCell(text);
  const muted = pending || display === '—';

  return (
    <div className="text-center">
      <span
        className="inline-flex items-center gap-1 rounded-[var(--r-md)] px-3 py-1 font-[var(--font-display)] text-[14.5px] font-bold tracking-[-0.02em]"
        style={{
          background: win ? 'var(--success-tint)' : 'transparent',
          color: muted ? 'var(--ink-4)' : win ? 'var(--success)' : 'var(--ink-1)',
          border: win
            ? '1px solid color-mix(in srgb, var(--success) 26%, transparent)'
            : '1px solid transparent',
        }}
      >
        {display}
        {star && display !== '—' ? <MpStar /> : null}
        {win ? <Icon kind="check" size={12} stroke={3} /> : null}
      </span>
    </div>
  );
}

function ProfileColumn({
  profile,
  accentClass,
  emptyMessage,
}: {
  profile: MpProfileCompleteness | null;
  accentClass: string;
  emptyMessage?: string;
}) {
  if (!profile) {
    return (
      <div className="flex min-h-[220px] items-center justify-center rounded-[var(--r-md)] border border-dashed border-[var(--cream-2)] bg-[var(--cream-0)] px-4 py-8 text-center text-[13px] leading-[1.55] text-[var(--ink-3)]">
        {emptyMessage ?? 'Competitor profile data is not available yet.'}
      </div>
    );
  }

  const tone = resolveMpTone(profile.tone);
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className={`text-[13.5px] font-semibold ${accentClass}`}>{profile.label}</span>
        <span
          className="font-[var(--font-display)] text-[22px] font-extrabold tracking-[-0.03em]"
          style={{ color: tone.accent }}
        >
          {profile.percent}%
        </span>
      </div>
      <div className="h-[7px] overflow-hidden rounded bg-[var(--surface-mute)]">
        <div
          className="h-full rounded"
          style={{ width: `${profile.percent}%`, background: tone.accent }}
        />
      </div>
      <MpProfileList items={profile.items} />
    </div>
  );
}

function EmptyVectorBlock({ message }: { message: string }) {
  return (
    <p className="rounded-[var(--r-md)] border border-dashed border-[var(--cream-2)] bg-[var(--cream-0)] px-4 py-3 text-[12.5px] leading-[1.6] text-[var(--ink-3)]">
      {message}
    </p>
  );
}

export interface MapPackIntelligenceViewProps {
  data?: MpMapPackData;
  loading?: boolean;
  onManageGbp?: () => void;
  onExport?: () => void;
}

export function MapPackIntelligenceView({
  data: dataProp,
  loading = false,
  onManageGbp,
  onExport,
}: MapPackIntelligenceViewProps = {}) {
  const meta = GROWTH_MAP_PACK_META;
  const controlled = dataProp !== undefined;
  const blocked = Boolean(dataProp?.blocked);
  const pending = dataProp?.dataStatus === 'pipeline_pending';

  const kpis = controlled ? dataProp.kpis : MP_KPIS;
  const benchmarkRows = controlled ? dataProp.benchmark.rows : MP_BENCH;
  const competitorName =
    (controlled ? dataProp.benchmark.competitorName : null) ?? meta.competitorName;
  const benchmarkInsight =
    (controlled ? dataProp.benchmark.insight : null) ??
    (controlled
      ? 'Competitor benchmark will populate when pipeline data is available.'
      : meta.benchmarkInsight);
  const attack = controlled ? dataProp.attackVector : MP_ATTACK;
  const threat = controlled ? dataProp.threatVector : MP_THREAT;
  const yourProfile = controlled ? dataProp.profile.yours : MP_YOUR_PROFILE;
  const compProfile = controlled ? dataProp.profile.competitor : MP_COMP_PROFILE;
  const reviewAudit = controlled ? dataProp.reviewAudit : MP_REVIEW_AUDIT;
  const aiBrief = controlled ? dataProp.aiBrief : { bottomLine: meta.aiBriefBottomLine, playbook: MP_PLAYBOOK };
  const footerBenchmarkNote = controlled
    ? dataProp.competitorsBenchmarkedCount != null
      ? `Benchmarked against ${dataProp.competitorsBenchmarkedCount} local competitors`
      : 'Competitor benchmark count unavailable'
    : meta.footerBenchmarkNote;
  const footerDataNote = controlled
    ? dataProp.dataFreshness
      ? `GBP data · ${dataProp.dataFreshness}`
      : meta.footerDataNote
    : meta.footerDataNote;

  return (
    <div className="growth-map-pack-root relative mx-auto w-full max-w-[1080px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-5">
        <div className="max-w-[620px]">
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="home" size={13} stroke={2} className="text-[var(--ai-3)]" />
            <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
              {meta.eyebrow}
            </span>
          </div>
          <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
            {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
          </h1>
          <p className="mt-2.5 text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            {meta.description}
            {loading ? ' · Updating…' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" iconLeft="pencil" onClick={onManageGbp}>
            Manage GBP
          </Button>
          <Button size="sm" variant="secondary" iconLeft="download" onClick={onExport}>
            Export report
          </Button>
        </div>
      </div>

      {blocked ? (
        <div className="mb-6 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--terracotta)_28%,transparent)] bg-[var(--terracotta-tint)] px-4 py-3 text-[13px] leading-[1.55] text-[var(--ink-1)]">
          Map Pack intelligence is temporarily unavailable — the backend endpoint is returning an
          error. Your clinic-side GBP data will appear here once the service is restored.
        </div>
      ) : null}

      {pending && !blocked ? (
        <div className="mb-6 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--gold)_28%,transparent)] bg-[var(--gold-tint)] px-4 py-3 text-[13px] leading-[1.55] text-[var(--ink-1)]">
          Competitor benchmark columns are still loading from the pipeline. Your clinic metrics,
          review audit, and profile completeness remain available below.
        </div>
      ) : null}

      <div
        className="mp-kpi-grid mb-6 grid gap-3"
        style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
      >
        {kpis.length ? (
          kpis.map((kpi) => {
            const tone = resolveMpTone(kpi.tone);
            const displayValue = kpi.value == null ? '—' : kpi.value;
            return (
              <MpCard key={kpi.label} className="px-[18px] py-4">
                <div className={mpEyebrowClass}>{kpi.label}</div>
                <div
                  className="mt-2.5 font-[var(--font-display)] font-extrabold leading-[1.05] tracking-[-0.04em]"
                  style={{
                    fontSize: kpi.small ? 19 : 27,
                    color: kpi.value == null ? 'var(--ink-4)' : tone.accent,
                  }}
                >
                  {displayValue}
                </div>
                <div className="mt-[7px] text-[11.5px] leading-[1.4] text-[var(--ink-3)]">{kpi.sub}</div>
              </MpCard>
            );
          })
        ) : (
          <MpCard className="col-span-full px-[18px] py-4 text-[13px] text-[var(--ink-3)]">
            KPI tiles will appear when Map Pack intelligence data is available.
          </MpCard>
        )}
      </div>

      <MpCard className="mb-6">
        <MpHead
          icon="bars"
          tone="ai"
          title="Head-to-head benchmark"
          sub="Your GBP vs. your #1 competitor and the local market average"
        />
        <div
          className="mp-bench-head mp-bench-grid grid border-b-2 border-[var(--cream-2)] pb-2.5"
          style={{ gridTemplateColumns: BENCH_COLS }}
        >
          <span className={mpEyebrowClass}>Metric</span>
          <span className={`${mpEyebrowClass} text-center text-[var(--ai-3)]`}>Your clinic</span>
          <span className={`${mpEyebrowClass} text-center text-[var(--ink-2)]`}>
            {competitorName} (#1)
          </span>
          <span className={`${mpEyebrowClass} text-center`}>Market avg</span>
        </div>
        {benchmarkRows.length ? (
          benchmarkRows.map((row, index) => (
            <div
              key={row.metric}
              className="mp-bench-row grid items-center py-3"
              style={{
                gridTemplateColumns: BENCH_COLS,
                borderBottom:
                  index < benchmarkRows.length - 1 ? '1px solid var(--cream-2)' : undefined,
              }}
            >
              <span className="text-[13px] font-medium text-[var(--ink-1)]">{row.metric}</span>
              <BenchCell text={row.yours} win={row.winner === 'you'} star={row.star === true} />
              <BenchCell
                text={row.comp}
                win={row.winner === 'comp'}
                star={row.star === true}
                pending={row.comp == null}
              />
              <div className="text-center">
                <span
                  className="inline-flex items-center gap-[3px] font-[var(--font-display)] text-[13.5px] font-medium"
                  style={{ color: row.avg == null ? 'var(--ink-4)' : 'var(--ink-4)' }}
                >
                  {formatCell(row.avg)}
                  {row.star && row.avg != null ? <MpStar color="var(--cream-shadow)" /> : null}
                </span>
              </div>
            </div>
          ))
        ) : (
          <p className="py-6 text-center text-[13px] text-[var(--ink-3)]">
            Benchmark rows will appear when competitor data is available.
          </p>
        )}
        <div className="mt-4 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--terracotta)_24%,transparent)] bg-[var(--terracotta-tint)] px-4 py-3">
          <div className="text-[13px] leading-[1.55] text-[var(--ink-1)]">{benchmarkInsight}</div>
        </div>
      </MpCard>

      <div
        className="mp-vector-grid mb-6 grid gap-4"
        style={{ gridTemplateColumns: '1fr 1fr' }}
      >
        <MpCard className="border-[color-mix(in_srgb,var(--success)_30%,transparent)]">
          <MpHead icon="target" tone="success" title="Attack vector" sub="Your strengths vs. their weaknesses" />
          {attack?.yourPraise?.length ? (
            <div className="mb-4">
              <MpVectorLabel tone="success">Your top praise</MpVectorLabel>
              <MpChips items={attack.yourPraise} tone="success" />
            </div>
          ) : null}
          {attack?.theirComplaints?.length ? (
            <div className="mb-4">
              <MpVectorLabel tone="terra">Their top complaints</MpVectorLabel>
              <MpChips items={attack.theirComplaints} tone="terra" />
            </div>
          ) : null}
          {!attack?.yourPraise?.length && !attack?.theirComplaints?.length ? (
            <div className="mb-4">
              <EmptyVectorBlock message="Attack vector themes will appear when competitor sentiment data is available." />
            </div>
          ) : null}
          {attack?.note ? (
            <div className="rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--success)_24%,transparent)] bg-[var(--success-tint)] p-3.5">
              <div className="mb-1.5 flex items-center gap-1.5">
                <Icon kind="target" size={14} stroke={2} className="text-[var(--success)]" />
                <span className="text-[12.5px] font-bold text-[var(--success)]">Opportunity</span>
              </div>
              <div className="text-[12.5px] leading-[1.6] text-[var(--ink-1)]">{attack.note}</div>
            </div>
          ) : null}
        </MpCard>

        <MpCard className="border-[color-mix(in_srgb,var(--terracotta)_30%,transparent)]">
          <MpHead icon="shield" tone="terra" title="Threat vector" sub="Their strengths vs. your weaknesses" />
          {threat?.theirPraise?.length ? (
            <div className="mb-4">
              <MpVectorLabel tone="success">Their top praise</MpVectorLabel>
              <MpChips items={threat.theirPraise} tone="success" />
            </div>
          ) : null}
          {threat?.yourComplaints?.length ? (
            <div className="mb-4">
              <MpVectorLabel tone="terra">Your top complaints</MpVectorLabel>
              <MpChips items={threat.yourComplaints} tone="terra" />
            </div>
          ) : null}
          {!threat?.theirPraise?.length && !threat?.yourComplaints?.length ? (
            <div className="mb-4">
              <EmptyVectorBlock message="Threat vector themes will appear when competitor sentiment data is available." />
            </div>
          ) : null}
          {threat?.note ? (
            <div className="rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--gold)_24%,transparent)] bg-[var(--gold-tint)] p-3.5">
              <div className="mb-1.5 flex items-center gap-1.5">
                <Icon kind="alert" size={14} stroke={2} className="text-[var(--gold)]" />
                <span className="text-[12.5px] font-bold text-[var(--gold)]">Threat</span>
              </div>
              <div className="text-[12.5px] leading-[1.6] text-[var(--ink-1)]">{threat.note}</div>
            </div>
          ) : null}
        </MpCard>
      </div>

      <MpCard className="mb-6">
        <MpHead
          icon="list"
          tone="info"
          title="Profile completeness & activity"
          sub="Google rewards active, fully completed profiles with higher Map Pack rankings"
        />
        <div className="mp-profile-grid grid grid-cols-2 gap-6">
          <ProfileColumn
            profile={yourProfile}
            accentClass="text-[var(--ai-3)]"
            emptyMessage="Your GBP profile completeness will appear when clinic data is synced."
          />
          <ProfileColumn
            profile={compProfile}
            accentClass="text-[var(--ink-2)]"
            emptyMessage="Competitor profile comparison is not populated by the backend yet."
          />
        </div>
        <div className="mt-[18px] flex flex-wrap items-center justify-between gap-3.5 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--gold)_24%,transparent)] bg-[var(--gold-tint)] px-[18px] py-3.5">
          <div className="min-w-[280px] flex-1 text-[13px] leading-[1.5] text-[var(--ink-1)]">
            {meta.profileGapNote}
          </div>
          <Button size="sm" variant="primary" icon="arrow-r">
            Manage profile
          </Button>
        </div>
      </MpCard>

      <MpCard className="mb-6">
        <MpHead
          icon="chat"
          tone="ai"
          title="Review response audit"
          sub="Unanswered reviews hurt your ranking and send the wrong message to prospective patients"
        />
        <div
          className="mp-audit-grid mb-[18px] grid gap-3"
          style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
        >
          {reviewAudit.length ? (
            reviewAudit.map((item) => {
              const tone = resolveMpTone(item.tone);
              return (
                <div
                  key={item.label}
                  className="rounded-[var(--r-md)] border p-4 text-center"
                  style={{
                    background: tone.tint,
                    borderColor: `color-mix(in srgb, ${tone.accent} 24%, transparent)`,
                  }}
                >
                  <div
                    className="font-[var(--font-display)] text-[28px] font-extrabold tracking-[-0.03em]"
                    style={{ color: tone.accent }}
                  >
                    {item.value}
                  </div>
                  <div className="mt-[5px] text-xs font-medium text-[var(--ink-1)]">{item.label}</div>
                  <div className="mt-0.5 text-[11px] text-[var(--ink-3)]">{item.sub}</div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full rounded-[var(--r-md)] border border-dashed border-[var(--cream-2)] px-4 py-6 text-center text-[13px] text-[var(--ink-3)]">
              Review response audit tiles will appear from your local GBP data.
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3.5 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-[18px] py-3.5">
          <div className="min-w-[280px] flex-1 text-[13px] leading-[1.6] text-[var(--ink-2)]">
            {meta.reviewAuditNote}
          </div>
          <Button size="sm" variant="primary" icon="arrow-r">
            Respond to 14 reviews
          </Button>
        </div>
      </MpCard>

      <div className="elva-grad-ink relative mb-[22px] overflow-hidden rounded-[var(--r-xl)] p-[26px_30px] text-white shadow-[var(--sh-2)]">
        <div className="elva-grad-spectrum-fill pointer-events-none absolute -right-10 -top-20 h-[260px] w-[260px] rounded-full opacity-[0.34] blur-[72px]" />
        <div className="relative">
          <div className="mb-3.5 flex items-center gap-2">
            <Icon kind="sparkle" size={16} stroke={2} className="text-white" />
            <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-white/80">
              AI Optimization Brief · Map Pack Dominance
            </span>
          </div>
          <div className="mb-5 rounded-[var(--r-md)] bg-white/10 p-[18px]">
            <div className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.06em] text-white/60">
              The bottom line
            </div>
            <div className="text-[14.5px] leading-[1.65] text-white/95">
              {aiBrief?.bottomLine ??
                'AI optimization brief will appear when competitor benchmark data is ready.'}
            </div>
          </div>
          <div className="mb-4 flex items-center gap-2">
            <Icon kind="target" size={15} stroke={2} className="text-white" />
            <span className="text-[13.5px] font-semibold">ELVA&apos;s recommended playbook</span>
          </div>
          <div className="flex flex-col gap-[11px]">
            {aiBrief?.playbook?.length ? (
              aiBrief.playbook.map((step) => (
                <div
                  key={step.num}
                  className="flex gap-3.5 rounded-[var(--r-md)] border-l-[3px] border-white/30 bg-white/[0.06] px-[17px] py-[15px]"
                >
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 font-[var(--font-display)] text-sm font-bold">
                    {step.num}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className="text-[13.5px] font-semibold">{step.title}</span>
                      <span className="rounded-[var(--r-pill)] bg-white/15 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.04em]">
                        {step.urgency}
                      </span>
                    </div>
                    <div className="text-[12.5px] leading-[1.6] text-white/80">{step.desc}</div>
                    {step.cta ? (
                      <button
                        type="button"
                        className="mt-2.5 inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--r-md)] border border-white/30 bg-white/10 px-[15px] py-[7px] font-[var(--font-body)] text-xs font-semibold text-white"
                      >
                        {step.cta} <Icon kind="arrow-r" size={12} stroke={2.2} />
                      </button>
                    ) : null}
                  </div>
                </div>
              ))
            ) : (
              <p className="rounded-[var(--r-md)] border border-white/20 bg-white/[0.06] px-4 py-3 text-[12.5px] leading-[1.6] text-white/80">
                Playbook steps will appear when the AI brief is generated from benchmark data.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--cream-2)] pt-4">
        <span className="font-[var(--font-mono)] text-xs text-[var(--ink-4)]">{footerDataNote}</span>
        <span className="text-xs text-[var(--ink-4)]">{footerBenchmarkNote}</span>
      </div>
    </div>
  );
}
